import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { istilah } from '../../../shared/lib/istilah'
import {
  useKatalogFitur, useKatalogModel, useTambahModel, useUbahModel,
} from '../../../domains/tenancy/api'
import {
  Baris, Bendera, Chip, Gagal, Isian, KepalaHalaman, Memuat, Panel, Pilihan, Tab, Tabel, Tombol,
} from '../../../shared/ui'

const PERAN = ['chat', 'text', 'image', 'video']
const TIER = ['hemat', 'seimbang', 'terbaik']

// Bentuk tarif per peran — cerminan BENTUK_TARIF di backend. Disalin dengan sengaja,
// dan backend tetap yang menegakkannya: layar yang menebak bentuknya sendiri lalu
// mengirim kunci yang salah akan ditolak 422, bukan diterima diam-diam.
const KOLOM_TARIF = {
  chat: ['input_per_juta', 'output_per_juta'],
  text: ['input_per_juta', 'output_per_juta'],
  image: ['per_gambar'],
  video: ['per_detik'],
}

function ringkasTarif(m, t) {
  const isi = Object.entries(m.tarif ?? {})
  if (isi.length === 0) return <span className="text-warn">{t('kt.tarifKosong')}</span>
  return (
    <span className="font-mono text-[11.5px]">
      {isi.map(([k, v]) => `${k.replace(/_/g, ' ')} $${v}`).join(' · ')}
    </span>
  )
}

/**
 * KEMAMPUAN satu model, ringkas — dan satu kendali: rasio bawaan untuk model gambar.
 *
 * Sumbernya kolom `ai_models` (ADR-073), yang sebagian besar diisi
 * `scripts/kemampuan_openrouter.py`. Yang bisa disunting di sini HANYA rasio bawaan:
 * kolom sinkron yang disunting tangan akan ditimpa diam-diam oleh sinkronisasi
 * berikutnya, dan backend memang tidak menerimanya.
 *
 * "Belum disinkron" DITULIS, bukan dibiarkan kosong. Sel kosong terbaca "model ini tidak
 * punya kemampuan apa pun", padahal artinya "belum pernah diperiksa".
 */
export function ringkasKemampuan(m, t) {
  const bagian = []
  if (m.has_image_input && m.has_text_output) bagian.push(t('kt.kemReferensi'))
  if (m.role === 'image' && m.has_reference_aspect_ratio === false) {
    bagian.push(t('kt.kemRasioReferensiAbaikan'))
  }
  if (m.role === 'video') {
    if (m.video_durations?.length) bagian.push(`${m.video_durations.join('/')} ${t('kt.kemDetik')}`)
    if (m.video_resolutions?.length) bagian.push(m.video_resolutions.join('/'))
    if (m.video_aspect_ratios?.length) bagian.push(m.video_aspect_ratios.join(' · '))
  }
  if (!m.capabilities_synced_at && bagian.length === 0) return t('kt.kemBelumSinkron')
  return bagian.join(' · ')
}

function Fitur({ t }) {
  const q = useKatalogFitur()
  if (q.isLoading) return <Memuat tinggi={5} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />
  return (
    <Panel judul={t('kt.fiturJudul')} catatan={t('kt.fiturCatatan')}>
      {/* Kolom KEADAAN, dan alasannya bukan kelengkapan. Fitur yang ditarik dari
          katalog (`aktif = false`) hilang dari layar Tenant Admin — kalau operator juga
          tidak bisa melihatnya di sini, ia hilang dari SEMUA layar dan tidak ada yang
          tahu ia pernah ada. `insight` ditarik 9 September karena fiturnya belum
          dibangun (BUGS XX-1). */}
      <Tabel kepala={[t('kt.kolFitur'), t('kt.kolButuh'), t('kt.kolDipakai'),
                      t('kt.kolKeadaan')]}>
        {(q.data ?? []).map((f) => (
          <Baris key={f.key}>
            {/* Kode dan nama jadi SATU kolom. Dua kolom untuk satu hal memakan lebar
                yang dibutuhkan kolom lain, dan kodenya cuma dibaca saat menelusuri
                kode — bukan saat menjawab "fitur apa ini". */}
            <td>
              <span className={f.aktif ? 'font-semibold' : 'font-semibold text-ink3'}>
                {f.name}
              </span>
              <span className="block font-mono text-[11px] text-ink3">{f.key}</span>
            </td>
            <td>
              {f.butuh.length === 0 ? (
                <span className="text-ink3">—</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  {f.butuh.map((b) => (
                    <span key={b} className="font-mono text-[11px] bg-surface2
                                             border border-line rounded px-1.5 py-0.5">
                      {b}
                    </span>
                  ))}
                </span>
              )}
            </td>
            {/* Menjawab "boleh tidak fitur ini saya tarik" tanpa membuka satu per satu
                tenant. Nol berarti tidak ada yang kehilangan apa pun kalau ditarik. */}
            <td className="tabular">
              {f.dipakai_tenant === 0
                ? <span className="text-ink3">{t('kt.takadaPemakai')}</span>
                : f.dipakai_tenant}
            </td>
            <td>
              <Chip nada={f.aktif ? 'ok' : 'idle'} titik={false}>
                {f.aktif ? t('kt.fiturDitawarkan') : t('kt.fiturDitarik')}
              </Chip>
            </td>
          </Baris>
        ))}
      </Tabel>
    </Panel>
  )
}

function Model({ t }) {
  const q = useKatalogModel()
  const tambah = useTambahModel()
  const ubah = useUbahModel()
  const [buka, setBuka] = useState(false)
  const [f, setF] = useState({ model_id: '', name: '', provider: '', role: 'chat', tier: '' })
  const [tarif, setTarif] = useState({})

  const kolom = KOLOM_TARIF[f.role] ?? []

  function kirim() {
    // Kunci tarif kosong dibuang, bukan dikirim sebagai "". Backend menolak nilai
    // bukan-angka, dan mengirim string kosong akan menghasilkan 422 yang membingungkan
    // untuk kolom yang memang sengaja dilewati.
    const bersih = Object.fromEntries(
      kolom.filter((k) => tarif[k] !== '' && tarif[k] !== undefined)
        .map((k) => [k, Number(tarif[k])]),
    )
    tambah.mutate(
      { ...f, tier: f.tier || null, tarif: bersih },
      {
        onSuccess: () => {
          setBuka(false)
          setF({ model_id: '', name: '', provider: '', role: 'chat', tier: '' })
          setTarif({})
        },
      },
    )
  }

  return (
    <>
      <Panel
        judul={t('kt.modelJudul')}
        catatan={t('kt.modelCatatan')}
        aksi={
          <Tombol onClick={() => { setBuka(!buka); tambah.reset() }}>
            {buka ? t('umum.batal') : t('kt.tambahModel')}
          </Tombol>
        }
      >
        {buka && (
          <div className="p-3.5 border-b border-line flex flex-col gap-3 max-w-[72ch]">
            <div className="grid grid-cols-2 gap-2.5">
              {[
                ['model_id', t('kt.kolId'), 'deepseek/deepseek-chat'],
                ['name', t('kt.kolNama'), 'DeepSeek Chat'],
                ['provider', t('kt.kolPenyedia'), 'deepseek'],
              ].map(([k, label, contoh]) => (
                <label key={k} className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                    {label}
                  </span>
                  <Isian
                    value={f[k]}
                    placeholder={contoh}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  />
                </label>
              ))}
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                  {t('kt.kolPeran')}
                </span>
                <Pilihan
                  value={f.role}
                  onChange={(e) => { setF({ ...f, role: e.target.value }); setTarif({}) }}
                >
                  {PERAN.map((r) => <option key={r} value={r}>{r}</option>)}
                </Pilihan>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                  {t('kt.kolTier')}
                </span>
                <Pilihan value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })}>
                  <option value="">{t('kt.tanpaTier')}</option>
                  {TIER.map((x) => <option key={x} value={x}>{x}</option>)}
                </Pilihan>
              </label>
              {/* Kolom tarif BERUBAH mengikuti peran. Menampilkan keempat kemungkinan
                  sekaligus akan mengundang mengisi yang tidak berlaku, dan backend
                  menolaknya — lebih baik ia tidak pernah muncul. */}
              {kolom.map((k) => (
                <label key={k} className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
                    {k.replace(/_/g, ' ')} (USD)
                  </span>
                  <Isian
                    type="number"
                    step="0.0001"
                    min="0"
                    value={tarif[k] ?? ''}
                    onChange={(e) => setTarif({ ...tarif, [k]: e.target.value })}
                  />
                </label>
              ))}
            </div>

            {tambah.isError && (
              <Bendera nada="bad" role="alert">{tambah.error?.message}</Bendera>
            )}
            <div>
              <Tombol
                disabled={!f.model_id.trim() || !f.name.trim() || !f.provider.trim() || tambah.isPending}
                onClick={kirim}
              >
                {tambah.isPending ? t('kt.menambah') : t('kt.simpanModel')}
              </Tombol>
            </div>
          </div>
        )}

        {q.isLoading && <Memuat tinggi={5} />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {q.data && (
          <Tabel
            kepala={[
              t('kt.kolModel'), t('kt.kolPenyedia'), t('kt.kolMunculDi'),
              t('kt.kolTarif'), t('kt.kolKemampuan'), t('kt.kolDipakai'), '',
            ]}
          >
            {q.data.map((m) => (
              <Baris key={m.id}>
                {/* Nama dan id jadi SATU kolom: keduanya menyebut model yang sama, dan
                    dua kolom untuk itu memakan lebar yang dibutuhkan Penyedia dan Tarif.
                    Tingkatan turun jadi keterangan di bawahnya — ia label, bukan sumbu. */}
                <td>
                  <span className={m.aktif ? 'font-semibold' : 'font-semibold text-ink3'}>
                    {m.name}
                  </span>
                  <span className="block font-mono text-[11px] text-ink3">{m.model_id}</span>
                </td>
                <td className="text-ink2">{m.provider}</td>
                {/* "Muncul di", bukan "Peran". `chat` dan `text` tidak menyampaikan apa
                    pun — model chat juga menghasilkan teks. Yang berguna bagi yang
                    membacanya: di layar mana model ini akan muncul. */}
                <td>
                  <Chip nada="idle" titik={false}>{istilah(t, 'munculDi', m.role)}</Chip>
                  {m.tier && (
                    <span className="block text-[11px] text-ink3">{m.tier}</span>
                  )}
                </td>
                <td>{ringkasTarif(m, t)}</td>
                <td className="text-[11.5px] text-ink2">
                  <span className="block">{ringkasKemampuan(m, t)}</span>
                  {/* RASIO BAWAAN — dipakai saat orang tidak memilih rasio. Disemai
                      sinkronisasi (auto kalau ada, lalu 4:5); pilihan di sini bertahan
                      melewati sinkronisasi selama rasionya masih diterbitkan. */}
                  {m.role === 'image' && m.image_aspect_ratios?.length > 0 && (
                    <label className="mt-1 flex items-center gap-1.5">
                      <span className="text-ink3">{t('kt.kemRasioBawaan')}</span>
                      <Pilihan
                        aria-label={`${t('kt.kemRasioBawaan')} ${m.model_id}`}
                        value={m.default_image_aspect_ratio ?? ''}
                        disabled={ubah.isPending}
                        onChange={(e) => ubah.mutate({
                          id: m.id, default_image_aspect_ratio: e.target.value,
                        })}
                      >
                        <option value="">{t('kt.kemRasioPenyedia')}</option>
                        {m.image_aspect_ratios.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </Pilihan>
                    </label>
                  )}
                </td>
                {/* Menjawab "boleh tidak model ini saya matikan" tanpa membuka satu
                    per satu tenant. */}
                <td className="tabular">{m.dipakai_tenant}</td>
                <td className="text-right">
                  <Tombol
                    kecil
                    jenis="sekunder"
                    disabled={ubah.isPending}
                    onClick={() => ubah.mutate({ id: m.id, aktif: !m.aktif })}
                  >
                    {m.aktif ? t('kt.nonaktifkan') : t('kt.aktifkan')}
                  </Tombol>
                </td>
              </Baris>
            ))}
          </Tabel>
        )}
      </Panel>

      {ubah.isError && (
        <Bendera nada="bad" role="alert">{ubah.error?.message}</Bendera>
      )}
    </>
  )
}

export default function Katalog() {
  const { t } = useBahasa()
  const [tab, setTab] = useState('model')
  return (
    <>
      <KepalaHalaman judul={t('kt.judul')} keterangan={t('kt.keterangan')} />
      <Tab
        aktif={tab}
        set={setTab}
        daftar={[
          { id: 'model', label: t('kt.tabModel') },
          { id: 'fitur', label: t('kt.tabFitur') },
        ]}
      />
      {tab === 'model' ? <Model t={t} /> : <Fitur t={t} />}
    </>
  )
}
